import { useCallback, useEffect, useState } from 'react';
import theme from '../adminTheme';
import {
  actualizarMarca,
  buscarMarcas,
  descartarPropuesta,
  editarPropuesta,
  enviarMarcasLiftingCast,
  listarPropuestas,
  previsualizarMarcasLiftingCast,
} from '../../../services/marcasService';

const MOTIVOS = {
  sin_cruce: 'sin atleta que coincida',
  cruce_ambiguo: 'más de un atleta posible',
  blanqueo: 'un movimiento en blanco',
  solo_movimiento: 'only bench, only deadlift u only squat',
  sin_division: 'sin división de powerlifting',
  sin_mejora: 'no superan la marca actual',
};

const kg = (valor) => (valor == null || valor === '' ? '' : String(valor));

const textoCambio = (cambio) => {
  if (cambio.movimiento === 'ranking') return `Puesto ${cambio.valor}`;
  const nombre = { squat: 'Sentadilla', bench_press: 'Banca', deadlift: 'Peso muerto' }[cambio.movimiento];
  return `${nombre} ${cambio.valor} kg`;
};

const MarcasPersonalesView = () => {
  const [url, setUrl] = useState('');
  const [tipoEvento, setTipoEvento] = useState('nacional');
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');

  const [buscar, setBuscar] = useState('');
  const [marcas, setMarcas] = useState([]);
  const [editando, setEditando] = useState(null);
  const [propuestas, setPropuestas] = useState([]);
  const [propuestaEdit, setPropuestaEdit] = useState({});

  const cargar = useCallback(async (texto) => {
    const [items, pendientes] = await Promise.all([
      buscarMarcas(texto || ''),
      listarPropuestas(),
    ]);
    setMarcas(items);
    setPropuestas(pendientes);
  }, []);

  useEffect(() => {
    cargar('').catch((e) => setError(e.message));
  }, [cargar]);

  const cambiarUrl = (valor) => {
    setUrl(valor);
    setPreview(null);
    setResultado(null);
  };

  const cambiarTipo = (tipo) => {
    setTipoEvento(tipo);
    setPreview(null);
    setResultado(null);
  };

  const previsualizar = async (event) => {
    event.preventDefault();
    setError('');
    setResultado(null);
    setCargando(true);
    try {
      const data = await previsualizarMarcasLiftingCast(url.trim(), tipoEvento);
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };

  const enviar = async () => {
    setError('');
    setEnviando(true);
    try {
      const data = await enviarMarcasLiftingCast(url.trim(), tipoEvento);
      setResultado(data);
      setPreview(null);
      await cargar(buscar);
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const guardarMarca = async () => {
    setError('');
    try {
      await actualizarMarca(editando.rut, {
        squat: editando.squat,
        bench_press: editando.bench_press,
        deadlift: editando.deadlift,
        ranking: editando.ranking,
        ranking_categoria: editando.ranking_categoria,
      });
      setEditando(null);
      await cargar(buscar);
    } catch (e) {
      setError(e.message);
    }
  };

  const guardarPropuesta = async (item) => {
    const borrador = propuestaEdit[item.id] || {};
    setError('');
    try {
      await editarPropuesta(item.id, {
        valor: borrador.valor ?? item.valor,
        usuario_rut: (borrador.usuario_rut || '').trim() || undefined,
      });
      setPropuestaEdit((prev) => ({ ...prev, [item.id]: undefined }));
      await cargar(buscar);
    } catch (e) {
      setError(e.message);
    }
  };

  const descartar = async (id) => {
    setError('');
    try {
      await descartarPropuesta(id);
      await cargar(buscar);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Actualizar marcas personales</h1>
          <p style={theme.subtitle}>
            Carga el LiftingCast para ver la tabla y después envía. No hay que confirmar atleta por atleta.
            El puesto solo se propone si la competencia es nacional.
          </p>
        </div>
      </div>

      <form onSubmit={previsualizar} style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        <input
          type="text"
          value={url}
          onChange={(e) => cambiarUrl(e.target.value)}
          placeholder="https://archive.liftingcast.com/meets/.../board"
          style={theme.input}
          aria-label="URL de LiftingCast"
          disabled={cargando || enviando}
        />
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="radio"
              name="tipo-evento"
              checked={tipoEvento === 'nacional'}
              onChange={() => cambiarTipo('nacional')}
            />
            Nacional
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="radio"
              name="tipo-evento"
              checked={tipoEvento === 'otro'}
              onChange={() => cambiarTipo('otro')}
            />
            Otro tipo de evento
          </label>
          <button type="submit" style={theme.btnPrimary} disabled={cargando || enviando || !url.trim()}>
            {cargando ? 'Cargando...' : 'Cargar LiftingCast'}
          </button>
        </div>
      </form>

      {error && <div style={theme.error}>{error}</div>}

      {resultado && (
        <div style={{ ...theme.success, marginBottom: 24 }}>
          <div>{resultado.mensaje}</div>
          <div style={{ marginTop: 8 }}>
            {resultado.propuestas} marcas propuestas
            {resultado.meet?.nombre ? ` · ${resultado.meet.nombre}` : ''}
            {resultado.tipo_evento === 'nacional' ? ' · con puesto' : ' · sin puesto'}
          </div>
          <div style={{ marginTop: 8 }}>
            {Object.entries(resultado.omitidos || {})
              .filter(([, n]) => n > 0)
              .map(([motivo, n]) => `${n} ${MOTIVOS[motivo] || motivo}`)
              .join(' · ') || 'Nadie quedó fuera.'}
          </div>
        </div>
      )}

      {preview && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h2 style={{ ...theme.title, fontSize: 20, margin: 0 }}>{preview.meet?.nombre || 'Previsualización'}</h2>
              <p style={{ ...theme.subtitle, marginTop: 4 }}>
                {[preview.meet?.fecha_texto, preview.meet?.federacion].filter(Boolean).join(' · ')}
                {` · ${preview.enviados} atletas recibirían una propuesta`}
                {preview.tipo_evento === 'nacional' ? ' · el puesto sí se envía' : ' · el puesto no se envía'}
              </p>
            </div>
            <button
              type="button"
              style={theme.btnPrimary}
              disabled={enviando || preview.enviados === 0}
              onClick={enviar}
            >
              {enviando ? 'Enviando...' : 'Enviar a los atletas'}
            </button>
          </div>
          <div style={{ overflow: 'auto', maxHeight: 520, border: '1px solid #eee', borderRadius: 8 }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  {['Atleta', 'Cuenta', 'Sentadilla', 'Banca', 'Peso muerto', 'Puesto', 'Se envía'].map((h) => (
                    <th key={h} style={{ ...theme.th, position: 'sticky', top: 0, background: '#fff' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(preview.filas || []).map((fila, indice) => (
                  <tr key={`${fila.nombre_liftingcast}-${indice}`}>
                    <td style={theme.td}>{fila.nombre_liftingcast}</td>
                    <td style={theme.td}>
                      {fila.atleta ? (
                        <>
                          <div>{fila.atleta.nombre}</div>
                          <div style={{ color: '#777', fontSize: 12 }}>{fila.atleta.rut}</div>
                        </>
                      ) : '—'}
                    </td>
                    <td style={theme.td}>{fila.squat ?? '—'}</td>
                    <td style={theme.td}>{fila.bench_press ?? '—'}</td>
                    <td style={theme.td}>{fila.deadlift ?? '—'}</td>
                    <td style={theme.td}>
                      {fila.ranking != null ? `#${fila.ranking}` : '—'}
                      {fila.ranking_categoria ? (
                        <div style={{ color: '#777', fontSize: 12 }}>{fila.ranking_categoria}</div>
                      ) : null}
                    </td>
                    <td style={theme.td}>
                      {fila.se_envia
                        ? (fila.cambios || []).map(textoCambio).join(', ')
                        : (MOTIVOS[fila.motivo] || fila.motivo || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2 style={{ ...theme.title, fontSize: 20, marginBottom: 12 }}>Marcas guardadas</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          cargar(buscar).catch((err) => setError(err.message));
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 16 }}
      >
        <input
          type="text"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder="Nombre, apellido o RUT"
          style={{ ...theme.input, maxWidth: 360 }}
          aria-label="Buscar atleta"
        />
        <button type="submit" style={theme.btnSecondary}>Buscar</button>
      </form>

      <div style={{ overflowX: 'auto', marginBottom: 32 }}>
        <table style={theme.table}>
          <thead>
            <tr>
              {['Atleta', 'Sentadilla', 'Banca', 'Peso muerto', 'Puesto', ''].map((h) => (
                <th key={h} style={theme.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {marcas.length === 0 && (
              <tr><td style={theme.td} colSpan={6}>No hay marcas para mostrar.</td></tr>
            )}
            {marcas.map((m) => {
              const activo = editando?.rut === m.rut;
              const fila = activo ? editando : m;
              return (
                <tr key={m.rut}>
                  <td style={theme.td}>
                    <div style={{ fontWeight: 600 }}>{m.nombre}</div>
                    <div style={{ color: '#777', fontSize: 12 }}>{m.rut}</div>
                  </td>
                  {['squat', 'bench_press', 'deadlift', 'ranking'].map((campo) => (
                    <td key={campo} style={theme.td}>
                      {activo ? (
                        <input
                          type="number"
                          min="0"
                          step={campo === 'ranking' ? '1' : '0.5'}
                          value={kg(fila[campo])}
                          onChange={(e) => setEditando({ ...fila, [campo]: e.target.value })}
                          style={{ ...theme.input, width: 90 }}
                          aria-label={campo}
                        />
                      ) : (
                        m[campo] ?? '—'
                      )}
                    </td>
                  ))}
                  <td style={theme.td}>
                    {activo ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" style={theme.btnPrimary} onClick={guardarMarca}>Guardar</button>
                        <button type="button" style={theme.btnSecondary} onClick={() => setEditando(null)}>Cancelar</button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        style={theme.btnSecondary}
                        onClick={() => setEditando({
                          ...m,
                          squat: kg(m.squat),
                          bench_press: kg(m.bench_press),
                          deadlift: kg(m.deadlift),
                          ranking: kg(m.ranking),
                          ranking_categoria: m.ranking_categoria || '',
                        })}
                      >
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 style={{ ...theme.title, fontSize: 20, marginBottom: 12 }}>Propuestas pendientes</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={theme.table}>
          <thead>
            <tr>
              {['Atleta', 'Marca', 'Valor', 'Competencia', ''].map((h) => (
                <th key={h} style={theme.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {propuestas.length === 0 && (
              <tr><td style={theme.td} colSpan={5}>No hay propuestas esperando respuesta.</td></tr>
            )}
            {propuestas.map((p) => {
              const borrador = propuestaEdit[p.id] || {};
              return (
                <tr key={p.id}>
                  <td style={theme.td}>
                    <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                    <div style={{ color: '#777', fontSize: 12 }}>{p.nombre_liftingcast}</div>
                    <input
                      type="text"
                      value={borrador.usuario_rut ?? ''}
                      onChange={(e) => setPropuestaEdit((prev) => ({
                        ...prev,
                        [p.id]: { ...borrador, usuario_rut: e.target.value },
                      }))}
                      placeholder="RUT correcto, si el cruce falló"
                      style={{ ...theme.input, marginTop: 6, maxWidth: 220 }}
                      aria-label={`RUT de ${p.nombre}`}
                    />
                  </td>
                  <td style={theme.td}>
                    {p.movimiento_label}
                    {p.categoria ? <div style={{ color: '#777', fontSize: 12 }}>{p.categoria}</div> : null}
                  </td>
                  <td style={theme.td}>
                    <input
                      type="number"
                      min="0"
                      step={p.movimiento === 'ranking' ? '1' : '0.5'}
                      value={borrador.valor ?? p.valor}
                      onChange={(e) => setPropuestaEdit((prev) => ({
                        ...prev,
                        [p.id]: { ...borrador, valor: e.target.value },
                      }))}
                      style={{ ...theme.input, width: 90 }}
                      aria-label={`Valor de ${p.movimiento_label}`}
                    />
                  </td>
                  <td style={theme.td}>
                    {p.meet_nombre || p.meet_id}
                    <div style={{ color: '#777', fontSize: 12 }}>
                      {p.tipo_evento === 'nacional' ? 'Nacional' : 'Otro'}
                    </div>
                  </td>
                  <td style={theme.td}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" style={theme.btnPrimary} onClick={() => guardarPropuesta(p)}>Guardar</button>
                      <button type="button" style={theme.btnDanger} onClick={() => descartar(p.id)}>Descartar</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarcasPersonalesView;
